// @ts-check
import * as Fingerprint from '@fingerprint/agent';
import { trackEvent } from 'user-analytics';
import { FINGERPRINT_REQUESTED } from 'constants/TrackEvents';
import errorMonitoring from '../../errorMonitoring';

const RETURNING_VISITOR_KEY = 'reservamos-returning_visitor';
const MODAL_SEEN_KEY = 'welcome-discount-modal-seen';

const MAX_CACHE_DURATION_SECONDS = 12 * 60 * 60;

/**
 * UserFingerprint is a function that initializes and retrieves the visitor fingerprint.
 * @returns {Object} - An object with the init and getFingerprint functions.
 */
const UserFingerprint = () => {
  let fpClientInstance;
  let instanceCacheName = 'reservamos-user_fingerprint_default';
  let instanceCacheTime = 7;
  let instanceTrackingKey = 'reservamos-user_fingerprint_default';
  let instanceEnabled = false;
  let staticUserFingerprint;
  let userMetadata = {
    tag: {},
  };

  /**
   * Notifies an error with the provided error and info.
   * @param {Object} options - The options for notifying the error.
   * @param {Error} options.error - The error to be notified.
   * @param {Object} options.info - Additional information about the error.
   */
  const notifyError = ({ error, info }) => {
    if (instanceEnabled)
      errorMonitoring.notify({
        error,
        info,
      });
  };

  /**
   * Validates the client before executing the provided function.
   * @param {Function} fn - The function to be executed.
   * @returns {Function} - The wrapped function with client validation.
   */
  const withClientValidation =
    (fn) =>
    (...args) => {
      if (!fpClientInstance) {
        notifyError({
          error: new Error('Fingerprint client not initialized'),
          info: { from: 'fingerprint withClientValidation' },
        });
        return Promise.resolve(null);
      }
      return fn(...args);
    };

  /**
   * Reads a non-expired identification from the application cache.
   * @returns {object|null} The cached Fingerprint result, or null when unavailable.
   */
  const readApplicationCache = () => {
    try {
      const cachedData = JSON.parse(localStorage.getItem(instanceCacheName));
      if (
        !cachedData?.body ||
        typeof cachedData.expiresAt !== 'number' ||
        cachedData.expiresAt <= Date.now() / 1000
      ) {
        if (cachedData) localStorage.removeItem(instanceCacheName);
        return null;
      }
      return {
        visitorId: cachedData.body.visitorId,
        requestId: cachedData.body.requestId,
        visitorFound: undefined,
      };
    } catch {
      return null;
    }
  };

  /**
   * Stores an identification for routes that are only allowed to read the application cache.
   * @param {object} fingerprint - The normalized Fingerprint result.
   */
  const writeApplicationCache = (fingerprint) => {
    try {
      localStorage.setItem(
        instanceCacheName,
        JSON.stringify({
          body: fingerprint,
          expiresAt: Date.now() / 1000 + instanceCacheTime * 24 * 60 * 60,
          cacheTime: instanceCacheTime,
        }),
      );
    } catch {
      // Fingerprint identification remains usable when localStorage is unavailable.
    }
  };

  /**
   * Sets the user information.
   * @param {Object} options - The options for setting the user information.
   * @param {string} options.linkedId - The linked ID of the user.
   * @param {object} options.tag - Tags to add to the user
   */
  const setUserMetadata = ({ linkedId, tag }) => {
    userMetadata = {
      ...userMetadata,
      ...(linkedId && { linkedId }),
      ...(tag && {
        tag: {
          ...userMetadata.tag,
          ...tag,
        },
      }),
    };
  };

  /**
   * Initializes the fingerprint client.
   * @param {Object} options - The options for initialization.
   * @param {string} options.key - The API key for the fingerprint client.
   * @param {string} options.customEndpoint - The custom URL for the fingerprint client.
   * @param {'us'|'eu'|'ap'} options.region - The Fingerprint workspace region.
   * @param {number} options.cacheTime - The time the cache will be saved.
   * @param {string} options.cacheName - The name of the cache key.
   * @param {string} options.trackingKey - The name of the prop/key to be set in the tracking events for fingerprint
   * @param {boolean} options.enabled - Indicates if the fingerprint is enabled
   */
  const init = async ({
    key,
    customEndpoint,
    region,
    cacheName,
    cacheTime,
    trackingKey,
    enabled,
  }) => {
    if (fpClientInstance) return;

    // Setting local variables
    instanceCacheName = cacheName || instanceCacheName;
    instanceCacheTime = cacheTime || instanceCacheTime;
    instanceTrackingKey = trackingKey || instanceTrackingKey;
    instanceEnabled = enabled;

    try {
      const client = Fingerprint.start({
        apiKey: key,
        ...(region && { region }),
        ...(customEndpoint && { endpoints: customEndpoint }),
        cache: {
          storage: 'localStorage',
          duration: Math.min(instanceCacheTime * 24 * 60 * 60, MAX_CACHE_DURATION_SECONDS),
          cachePrefix: instanceCacheName,
        },
      });
      if (!client || typeof client.get !== 'function') {
        throw new Error('Fingerprint client failed to initialize');
      }
      fpClientInstance = client;
    } catch (error) {
      fpClientInstance = undefined;
      notifyError({
        error: new Error(error),
        info: { from: 'fingerprint init' },
      });
      return null;
    }
  };

  /**
   * Gets the visitor fingerprint.
   */
  const setUpFingerprint = withClientValidation(async () => {
    try {
      const {
        visitor_id: visitorId,
        event_id: requestId,
        cache_hit: cacheHit,
      } = await fpClientInstance.get({
        ...userMetadata,
      });
      if (!visitorId) return null;

      const fingerprint = {
        visitorId,
        requestId,
        visitorFound: undefined,
      };

      if (!cacheHit) {
        trackEvent(FINGERPRINT_REQUESTED, {
          'User Fingerprint': visitorId,
        });
      }
      const applicationCache = readApplicationCache();
      if (!cacheHit || applicationCache?.visitorId !== visitorId) {
        writeApplicationCache(fingerprint);
      }
      staticUserFingerprint = visitorId;
      return fingerprint;
    } catch (error) {
      notifyError({
        error: new Error(error),
        info: { from: 'fingerprint getFingerprint' },
      });
      return null;
    }
  });

  /**
   * Returns the cached fingerprint.
   */
  const getFingerprintFromCache = async () => {
    if (!fpClientInstance) return null;

    const cachedFingerprint = readApplicationCache();
    if (!cachedFingerprint?.visitorId) return null;

    staticUserFingerprint = cachedFingerprint.visitorId;
    return cachedFingerprint;
  };

  /**
   * Gets the tracking key.
   * @returns {string} The tracking key.
   */
  const getTrackingKey = () => instanceTrackingKey;

  /**
   * Gets the user fingerprint.
   * @returns {string} The user fingerprint.
   */
  const getFingerprint = () => staticUserFingerprint;

  /**
   * Safely gets a value from localStorage.
   * @param {string} key - The localStorage key.
   * @returns {string|null} The value or null if not found/error.
   */
  const getFromStorage = (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  /**
   * Safely sets a value in localStorage.
   * @param {string} key - The localStorage key.
   * @param {string} value - The value to store.
   */
  const setInStorage = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Silent fail if localStorage is not available
    }
  };

  /**
   * Marks the user as a returning visitor. Called when user closes the discount modal.
   */
  const markAsReturningVisitor = () => setInStorage(RETURNING_VISITOR_KEY, 'true');

  /**
   * Checks the explicit development override for the new-user discount.
   * Agent v4 no longer exposes visitorFound, so ordinary visits are not marked as new.
   * @returns {boolean} True only when the development override is enabled.
   */
  const isFirstSession = () => process.env.RESERVAMOS_PUBLIC_FORCE_NEW_USER_DETECTED === 'true';

  /**
   * Checks if the discount modal has already been shown to the user.
   * @returns {boolean} True if the modal was already shown.
   */
  const hasSeenModal = () => getFromStorage(MODAL_SEEN_KEY) === 'true';

  /**
   * Marks the discount modal as shown to prevent showing it again.
   */
  const markModalSeen = () => setInStorage(MODAL_SEEN_KEY, 'true');

  return {
    init,
    setUpFingerprint,
    getFingerprint,
    getCachedFingerprint: getFingerprintFromCache,
    setUserMetadata,
    getTrackingKey,
    markAsReturningVisitor,
    isFirstSession,
    hasSeenModal,
    markModalSeen,
  };
};

const fingerprintService = UserFingerprint();
export default fingerprintService;
